import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, DatePicker, Empty, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  HistoryOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { firestore } from "../../../../configs/FirebaseConfig";
import {
  FIRESTORE_RECONCILIATION_RUNS_COLLECTION,
  FIRESTORE_REGIONS_TABLE,
} from "../../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../../constants/CompanyConstants";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import urls from "urls";
import Loading from "components/shared-components/Loading";
import Flex from "components/shared-components/Flex";
import useIsMobile from "hooks/useIsMobile";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const ReconciliationLog = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobileView = useIsMobile();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(null);
  const [scopeFilter, setScopeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    const runsQuery = query(
      collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_RECONCILIATION_RUNS_COLLECTION),
      orderBy("timestamp", "desc"),
    );

    const unsubscribe = onSnapshot(
      runsQuery,
      (snapshot) => {
        const runsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRuns(runsData);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching reconciliation runs:", error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  const filteredRuns = useMemo(() => {
    let filtered = [...runs];

    // Apply date range filter
    if (dateRange && dateRange.length === 2) {
      const startDate = dayjs(dateRange[0]).startOf("day");
      const endDate = dayjs(dateRange[1]).endOf("day");
      filtered = filtered.filter((run) => {
        const runDate = dayjs(run.timestamp * 1000);
        return runDate.isSameOrAfter(startDate) && runDate.isSameOrBefore(endDate);
      });
    }

    // Apply scope filter
    if (scopeFilter !== "all") {
      filtered = filtered.filter((run) => run.scope === scopeFilter);
    }

    // Apply status filter (has discrepancies or not)
    if (statusFilter === "with_discrepancies") {
      filtered = filtered.filter((run) => run.discrepancyCount > 0);
    } else if (statusFilter === "no_discrepancies") {
      filtered = filtered.filter((run) => run.discrepancyCount === 0);
    }

    return filtered;
  }, [runs, dateRange, scopeFilter, statusFilter]);

  const handleViewDetails = (runId) => {
    navigate(urls.reconciliationDetail + runId);
  };

  const getScopeTag = (scope) => {
    if (scope === "week") {
      return <Tag color="blue">{t("reconciliation_scope_week")}</Tag>;
    }
    if (scope === "full") {
      return <Tag color="purple">{t("reconciliation_scope_full")}</Tag>;
    }
    return <Tag>{scope}</Tag>;
  };

  const getStatusBadge = (discrepancyCount) => {
    if (discrepancyCount === 0) {
      return (
        <Badge
          status="success"
          text={
            <Space>
              <CheckCircleOutlined style={{ color: "#52c41a" }} />
              <Text type="success">{t("reconciliation_no_issues")}</Text>
            </Space>
          }
        />
      );
    }
    if (discrepancyCount <= 5) {
      return (
        <Badge
          status="warning"
          text={
            <Space>
              <WarningOutlined style={{ color: "#faad14" }} />
              <Text type="warning">
                {discrepancyCount} {t("reconciliation_issues")}
              </Text>
            </Space>
          }
        />
      );
    }
    return (
      <Badge
        status="error"
        text={
          <Space>
            <ExclamationCircleOutlined style={{ color: "#f5222d" }} />
            <Text type="danger">
              {discrepancyCount} {t("reconciliation_issues")}
            </Text>
          </Space>
        }
      />
    );
  };

  const columns = [
    {
      title: t("reconciliation_run_date"),
      dataIndex: "timestamp",
      key: "timestamp",
      width: 180,
      render: (timestamp, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{dayjs(timestamp * 1000).format("DD-MM-YYYY HH:mm")}</Text>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {dayjs(timestamp * 1000).fromNow()}
          </Text>
          {isMobileView && getScopeTag(record.scope)}
        </Space>
      ),
      sorter: (a, b) => a.timestamp - b.timestamp,
      defaultSortOrder: "descend",
    },
    {
      title: t("reconciliation_scope"),
      dataIndex: "scope",
      key: "scope",
      width: 120,
      responsive: ["lg"],
      render: (scope) => getScopeTag(scope),
      filters: [
        { text: t("reconciliation_scope_week"), value: "week" },
        { text: t("reconciliation_scope_full"), value: "full" },
      ],
      onFilter: (value, record) => record.scope === value,
    },
    {
      title: t("reconciliation_status"),
      key: "status",
      width: 180,
      render: (_, record) => getStatusBadge(record.discrepancyCount),
    },
    {
      title: t("reconciliation_summary"),
      key: "summary",
      responsive: ["lg"],
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Text>
            {t("reconciliation_dates_checked")}: <Text strong>{record.datesChecked}</Text>
          </Text>
          <Text>
            {t("reconciliation_orders_scanned")}: <Text strong>{record.ordersScanned}</Text>
          </Text>
          {record.discrepancyCount > 0 && (
            <Text type="danger">
              {t("reconciliation_discrepancies_found")}:{" "}
              <Text strong type="danger">
                {record.discrepancyCount}
              </Text>
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: t("reconciliation_horizon"),
      key: "horizon",
      width: 150,
      responsive: ["lg"],
      render: (_, record) => (
        <Tooltip title={`${record.horizonDays + 1} ${t("reconciliation_days_total")}`}>
          <Tag icon={<HistoryOutlined />}>
            {record.horizonDays + 1} {t("reconciliation_days")}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: t("actions"),
      key: "actions",
      width: 120,
      responsive: ["lg"],
      render: (_, record) => (
        <Button type="primary" icon={<EyeOutlined />} onClick={() => handleViewDetails(record.runId)} size="small">
          {t("view_details")}
        </Button>
      ),
    },
  ];

  const clearFilters = () => {
    setDateRange(null);
    setScopeFilter("all");
    setStatusFilter("all");
  };

  if (loading) {
    return <Loading />;
  }

  const hasActiveFilters = dateRange || scopeFilter !== "all" || statusFilter !== "all";

  return (
    <Card>
      <Flex alignItems="center" justifyContent="between" mobileFlex={false} className="mb-4">
        <Space direction="vertical" size={0}>
          <h3 className="mb-0">
            <HistoryOutlined style={{ marginRight: 8 }} />
            {t("reconciliation_log_title")}
          </h3>
          <Text type="secondary">{t("reconciliation_log_subtitle")}</Text>
        </Space>
        <Space>{hasActiveFilters && <Button onClick={clearFilters}>{t("clear_filters")}</Button>}</Space>
      </Flex>

      <Space wrap className="mb-4" style={{ width: "100%" }} direction={isMobileView ? "vertical" : "horizontal"}>
        <RangePicker
          style={isMobileView ? { width: "100%" } : undefined}
          value={dateRange}
          onChange={setDateRange}
          format="DD-MM-YYYY"
          placeholder={[t("start_date"), t("end_date")]}
        />
        <Select
          value={scopeFilter}
          onChange={setScopeFilter}
          style={{ width: isMobileView ? "100%" : 150 }}
          placeholder={t("reconciliation_filter_scope")}
        >
          <Option value="all">{t("all_scopes")}</Option>
          <Option value="week">{t("reconciliation_scope_week")}</Option>
          <Option value="full">{t("reconciliation_scope_full")}</Option>
        </Select>
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: isMobileView ? "100%" : 180 }}
          placeholder={t("reconciliation_filter_status")}
        >
          <Option value="all">{t("all_statuses")}</Option>
          <Option value="with_discrepancies">{t("reconciliation_with_discrepancies")}</Option>
          <Option value="no_discrepancies">{t("reconciliation_no_discrepancies")}</Option>
        </Select>
      </Space>

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Text type="secondary">
            {t("showing")} {filteredRuns.length} {t("of")} {runs.length} {t("reconciliation_runs")}
          </Text>
          {filteredRuns.some((r) => r.discrepancyCount > 0) && (
            <Badge
              count={filteredRuns.filter((r) => r.discrepancyCount > 0).length}
              style={{ backgroundColor: "#f5222d" }}
            />
          )}
        </Space>
      </div>

      {filteredRuns.length === 0 ? (
        <Empty description={t("no_reconciliation_runs_found")} />
      ) : (
        <Table
          columns={columns}
          dataSource={filteredRuns}
          rowKey="runId"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `${t("total")} ${total} ${t("items")}`,
          }}
          scroll={isMobileView ? undefined : { x: 800 }}
          onRow={(record) => (isMobileView ? { onClick: () => handleViewDetails(record.runId) } : {})}
          rowClassName={isMobileView ? "cursor-pointer" : undefined}
        />
      )}
    </Card>
  );
};

export default ReconciliationLog;
